import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, Pencil, Trash2, Play, Repeat, Check } from 'lucide-react';

const INTERVALL_LABEL = {
  ukentlig: 'Ukentlig', manedlig: 'Månedlig', kvartalsvis: 'Kvartalsvis',
  halvarlig: 'Halvårlig', arlig: 'Årlig',
};

const tomLinje = () => ({ beskrivelse: '', antall: 1, enhetspris: '', mva_sats: 25 });
const tomMal = () => ({
  navn: '', kunde_navn: '', kunde_adresse: '', kunde_postnr: '', kunde_sted: '',
  kunde_orgnr: '', kunde_epost: '', kunde_telefon: '',
  intervall: 'manedlig', neste_dato: new Date().toISOString().slice(0, 10),
  forfallsdager: 14, aktiv: true, auto_send: false, notater: '',
  linjer: [tomLinje()],
});

const Repeterende = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(tomMal());
  const [redigerer, setRedigerer] = useState(null);
  const [feil, setFeil] = useState('');
  const [kjorRes, setKjorRes] = useState(null);

  const last = () => api.get('/repeterende').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const aapneNy = () => { setRedigerer(null); setForm(tomMal()); setFeil(''); setOpen(true); };
  const aapneRediger = (r) => {
    setRedigerer(r);
    setForm({
      ...r,
      linjer: r.linjer && r.linjer.length ? r.linjer.map((l) => ({
        beskrivelse: l.beskrivelse, antall: l.antall, enhetspris: l.enhetspris, mva_sats: l.mva_sats,
      })) : [tomLinje()],
    });
    setFeil(''); setOpen(true);
  };
  const lagre = async () => {
    setFeil('');
    try {
      if (redigerer) await api.put(`/repeterende/${redigerer.id}`, form);
      else await api.post('/repeterende', form);
      setOpen(false); last();
    } catch (e) { setFeil(e.response?.data?.error || 'Feil.'); }
  };
  const slett = async (r) => {
    if (!window.confirm(`Slett mal "${r.navn}"?`)) return;
    await api.delete(`/repeterende/${r.id}`); last();
  };
  const kjor = async () => {
    setKjorRes(null);
    const r = await api.post('/repeterende/kjor');
    setKjorRes(r.data);
    last();
  };

  const oppdLinje = (i, key, val) =>
    setForm({ ...form, linjer: form.linjer.map((l, idx) => idx === i ? { ...l, [key]: val } : l) });
  const slettLinje = (i) => setForm({ ...form, linjer: form.linjer.filter((_, idx) => idx !== i) });
  const leggTilLinje = () => setForm({ ...form, linjer: [...form.linjer, tomLinje()] });

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader
        title="Abonnement / repeterende fakturaer"
        subtitle="Maler som auto-genererer fakturaer på fast intervall"
        actions={<>
          <button className="btn-ghost" onClick={kjor}><Play size={14} /> Kjør nå</button>
          <button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Ny mal</button>
        </>}
      />

      {kjorRes && (
        <Card style={{ marginBottom: 12 }}>
          <div className="flex items-center" style={{ gap: 6 }}>
            <Check size={14} style={{ color: 'var(--theme-success)' }} />
            <strong style={{ fontSize: 12 }}>
              {kjorRes.antall === 0 ? 'Ingen maler trengte å kjøres.' : `Genererte ${kjorRes.antall} faktura(er):`}
            </strong>
          </div>
          {kjorRes.laget?.length > 0 && (
            <ul style={{ fontSize: 11, margin: '6px 0 0 18px' }}>
              {kjorRes.laget.map((x) => (<li key={x.id}>{x.nr} – {x.mal}</li>))}
            </ul>
          )}
        </Card>
      )}

      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Navn</th><th>Kunde</th><th>Intervall</th><th>Neste</th>
            <th style={{ textAlign: 'right' }}>Generert</th>
            <th>Status</th><th style={{ width: 80 }}></th>
          </tr></thead>
          <tbody>
            {liste.length === 0 && (
              <tr><td colSpan={7} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>
                Ingen maler enda. Lag en mal for å fakturere abonnement automatisk.
              </td></tr>
            )}
            {liste.map((r) => (
              <tr key={r.id}>
                <td><Repeat size={11} style={{ marginRight: 4, color: 'var(--theme-accent)' }} />{r.navn}</td>
                <td>{r.kunde_navn}</td>
                <td>{INTERVALL_LABEL[r.intervall] || r.intervall}</td>
                <td>{dato(r.neste_dato)}</td>
                <td style={{ textAlign: 'right' }}>{r.antall_generert}</td>
                <td>
                  {r.aktiv
                    ? <span className="badge badge-success">aktiv</span>
                    : <span className="badge badge-neutral">pause</span>}
                </td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <button className="btn-ghost" onClick={() => aapneRediger(r)}><Pencil size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(r)}
                            style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger mal' : 'Ny abonnement-mal'} width={780}>
        {feil && <div className="badge badge-danger" style={{ padding: 6, marginBottom: 8 }}>{feil}</div>}
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label>Internt navn</label>
            <input value={form.navn} onChange={(e) => setForm({ ...form, navn: e.target.value })}
                   placeholder="F.eks. Måndlig konsulenttime" />
          </div>
          <div>
            <label>Kundenavn *</label>
            <input value={form.kunde_navn} onChange={(e) => setForm({ ...form, kunde_navn: e.target.value })} />
          </div>
          <div>
            <label>Adresse</label>
            <input value={form.kunde_adresse} onChange={(e) => setForm({ ...form, kunde_adresse: e.target.value })} />
          </div>
          <div>
            <label>Org.nr</label>
            <input value={form.kunde_orgnr} onChange={(e) => setForm({ ...form, kunde_orgnr: e.target.value })} />
          </div>
          <div>
            <label>Postnr / sted</label>
            <div className="flex" style={{ gap: 4 }}>
              <input value={form.kunde_postnr} onChange={(e) => setForm({ ...form, kunde_postnr: e.target.value })}
                     style={{ width: 90 }} />
              <input value={form.kunde_sted} onChange={(e) => setForm({ ...form, kunde_sted: e.target.value })}
                     style={{ flex: 1 }} />
            </div>
          </div>
          <div>
            <label>E-post</label>
            <input value={form.kunde_epost} onChange={(e) => setForm({ ...form, kunde_epost: e.target.value })} />
          </div>
          <div>
            <label>Intervall</label>
            <select value={form.intervall} onChange={(e) => setForm({ ...form, intervall: e.target.value })}>
              {Object.entries(INTERVALL_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Neste fakturadato *</label>
            <input type="date" value={form.neste_dato}
                   onChange={(e) => setForm({ ...form, neste_dato: e.target.value })} />
          </div>
          <div>
            <label>Forfallsdager</label>
            <input type="number" value={form.forfallsdager}
                   onChange={(e) => setForm({ ...form, forfallsdager: parseInt(e.target.value, 10) || 14 })} />
          </div>
          <div className="flex items-center" style={{ gap: 12, marginTop: 18 }}>
            <label className="flex items-center" style={{ gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={form.aktiv}
                     onChange={(e) => setForm({ ...form, aktiv: e.target.checked })} />
              Aktiv
            </label>
            <label className="flex items-center" style={{ gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={form.auto_send}
                     onChange={(e) => setForm({ ...form, auto_send: e.target.checked })} />
              Send på e-post automatisk
            </label>
          </div>
        </div>

        <h4 style={{ marginTop: 12, marginBottom: 4, fontSize: 12 }}>Linjer</h4>
        <table>
          <thead><tr>
            <th>Beskrivelse</th>
            <th style={{ width: 70 }}>Antall</th><th style={{ width: 100 }}>Pris</th>
            <th style={{ width: 70 }}>MVA %</th><th style={{ width: 30 }}></th>
          </tr></thead>
          <tbody>
            {form.linjer.map((l, i) => (
              <tr key={i}>
                <td><input value={l.beskrivelse} onChange={(e) => oppdLinje(i, 'beskrivelse', e.target.value)} /></td>
                <td><input value={l.antall} onChange={(e) => oppdLinje(i, 'antall', e.target.value)} style={{ textAlign: 'right' }} /></td>
                <td><input value={l.enhetspris} onChange={(e) => oppdLinje(i, 'enhetspris', e.target.value)} style={{ textAlign: 'right' }} /></td>
                <td>
                  <select value={l.mva_sats} onChange={(e) => oppdLinje(i, 'mva_sats', parseFloat(e.target.value))}>
                    <option value={25}>25</option><option value={15}>15</option>
                    <option value={12}>12</option><option value={0}>0</option>
                  </select>
                </td>
                <td><button className="btn-ghost" onClick={() => slettLinje(i)} disabled={form.linjer.length === 1}
                            style={{ color: 'var(--theme-danger)' }}><Trash2 size={11} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn-ghost" onClick={leggTilLinje} style={{ marginTop: 6 }}>
          <Plus size={12} /> Ny linje
        </button>

        <div className="flex" style={{ gap: 6, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
          <button className="btn-primary" onClick={lagre}>Lagre</button>
        </div>
      </Modal>
    </>
  );
};

export default Repeterende;
